import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Building2, MapPin } from 'lucide-react';
import { tenantService } from '../../services';
import type { Tenant, UpdateTenantRequest, DropdownItem, CountryItem, StateItem, CityItem } from '../../types';

const EditTenantPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Original tenant data
  const [tenant, setTenant] = useState<Tenant | null>(null);

  // Dropdown data
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [states, setStates] = useState<StateItem[]>([]);
  const [cities, setCities] = useState<CityItem[]>([]);
  const [businessTypes, setBusinessTypes] = useState<DropdownItem[]>([]);
  const [subBusinessTypes, setSubBusinessTypes] = useState<DropdownItem[]>([]);

  // Loading states for cascading dropdowns
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingSubBusiness, setIsLoadingSubBusiness] = useState(false);

  // Form data
  const [formData, setFormData] = useState<UpdateTenantRequest>({
    company_name: '',
    company_code: '',
    email: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    country_id: 0,
    state_id: 0,
    city_id: 0,
    postal_code: '',
    business_type_id: 0,
    sub_business_id: undefined,
    status: true,
  });

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        // Load tenant and dropdowns in parallel
        const [tenantRes, countriesRes, businessTypesRes] = await Promise.all([
          tenantService.getTenantById(Number(id)),
          tenantService.getCountries(),
          tenantService.getBusinessTypes(),
        ]);

        console.log('Tenant data (full object):', JSON.stringify(tenantRes, null, 2));
        console.log('Business types:', businessTypesRes);
        console.log('Tenant business_type_id:', tenantRes.business_type_id);
        console.log('Tenant postal_code:', tenantRes.postal_code);
        // Log all keys to see actual field names from API
        console.log('Tenant object keys:', Object.keys(tenantRes));

        setTenant(tenantRes);
        setCountries(countriesRes.items || []);
        setBusinessTypes(businessTypesRes.items || []);

        // Set form data from tenant
        // API returns 'business_type' not 'business_type_id', and 'address1'/'address2' not 'address_line_1'/'address_line_2'
        const businessTypeValue = (tenantRes as any).business_type || tenantRes.business_type_id || 0;
        const countryValue = tenantRes.country_id || (tenantRes as any).country || 0;
        const stateValue = tenantRes.state_id || (tenantRes as any).state || 0;
        const cityValue = tenantRes.city_id || (tenantRes as any).city || 0;
        const postalCodeValue = tenantRes.postal_code || (tenantRes as any).postalcode || '';
        const address1Value = (tenantRes as any).address1 || tenantRes.address_line_1 || '';
        const address2Value = (tenantRes as any).address2 || tenantRes.address_line_2 || '';
        const statusValue = tenantRes.status === true || (tenantRes as any).status === 'Y';

        console.log('Mapped values - business_type:', businessTypeValue, 'country:', countryValue, 'state:', stateValue, 'city:', cityValue, 'postal_code:', postalCodeValue);

        setFormData({
          company_name: tenantRes.company_name || '',
          company_code: tenantRes.company_code || '',
          email: tenantRes.email || '',
          phone: tenantRes.phone || '',
          address_line_1: address1Value,
          address_line_2: address2Value,
          country_id: countryValue,
          state_id: stateValue,
          city_id: cityValue,
          postal_code: postalCodeValue,
          business_type_id: businessTypeValue,
          sub_business_id: (tenantRes as any).sub_business || tenantRes.sub_business_id || undefined,
          status: tenantRes.status,
        });

        // Load sub-business types if business type is set
        if (businessTypeValue) {
          const subBusinessRes = await tenantService.getSubBusinessTypes(businessTypeValue);
          setSubBusinessTypes(subBusinessRes.items || []);
        }

        // Load states if country is set
        if (countryValue) {
          const statesRes = await tenantService.getStates(countryValue);
          setStates(statesRes.items || []);

          // Load cities if state is set
          if (stateValue) {
            const citiesRes = await tenantService.getCities(stateValue);
            setCities(citiesRes.items || []);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tenant data');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [id]);

  // Load states when country changes
  const handleCountryChange = async (countryId: number) => {
    setFormData(prev => ({ ...prev, country_id: countryId, state_id: 0, city_id: 0 }));
    setStates([]);
    setCities([]);

    if (countryId > 0) {
      setIsLoadingStates(true);
      try {
        const statesRes = await tenantService.getStates(countryId);
        setStates(statesRes.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load states');
      } finally {
        setIsLoadingStates(false);
      }
    }
  };

  // Load sub-business types when business type changes
  const handleBusinessTypeChange = async (businessTypeId: number) => {
    setFormData(prev => ({ ...prev, business_type_id: businessTypeId, sub_business_id: undefined }));
    setSubBusinessTypes([]);

    if (businessTypeId > 0) {
      setIsLoadingSubBusiness(true);
      try {
        const subBusinessRes = await tenantService.getSubBusinessTypes(businessTypeId);
        setSubBusinessTypes(subBusinessRes.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sub-business types');
      } finally {
        setIsLoadingSubBusiness(false);
      }
    }
  };

  // Load cities when state changes
  const handleStateChange = async (stateId: number) => {
    setFormData(prev => ({ ...prev, state_id: stateId, city_id: 0 }));
    setCities([]);

    if (stateId > 0) {
      setIsLoadingCities(true);
      try {
        const citiesRes = await tenantService.getCities(stateId);
        setCities(citiesRes.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load cities');
      } finally {
        setIsLoadingCities(false);
      }
    }
  };

  const handleInputChange = (field: keyof UpdateTenantRequest, value: string | number | boolean | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!id) return;

    // Validation
    if (!formData.company_name?.trim()) {
      setError('Tenant name is required');
      return;
    }
    if (!formData.email?.trim()) {
      setError('Email is required');
      return;
    }

    setIsSaving(true);
    try {
      await tenantService.updateTenant(Number(id), formData);
      setSuccess('Tenant updated successfully!');
      setTimeout(() => {
        navigate('/tenants');
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-96">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  if (!tenant) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-96">
          <h2 className="text-xl font-semibold text-text-primary mb-2">Tenant Not Found</h2>
          <p className="text-text-muted mb-4">The tenant you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/tenants')}>Back to Tenants</Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/tenants')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Edit Tenant</h1>
            <p className="text-text-muted mt-1">{tenant.company_name}</p>
          </div>
        </div>

        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {success && (
          <Alert variant="success" message={success} />
        )}

        <form onSubmit={handleSubmit}>
          {/* Company Information */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <Building2 size={20} className="text-accent-primary" />
                <h2 className="text-lg font-semibold text-text-primary">Company Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Tenant Name *"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Enter tenant name"
                  fullWidth
                />
                <Input
                  label="Company Code"
                  value={formData.company_code}
                  placeholder="e.g., COMP-001"
                  fullWidth
                  disabled
                />
                <Input
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="company@example.com"
                  fullWidth
                />
                <Input
                  label="Phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  fullWidth
                />
              </div>

              <div className="mt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.status === true || formData.status === 'Y'}
                    onChange={(e) => handleInputChange('status', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
                  />
                  <span className="text-sm text-text-secondary">Active</span>
                </label>
              </div>
            </div>
          </Card>

          {/* Address Information */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <MapPin size={20} className="text-accent-primary" />
                <h2 className="text-lg font-semibold text-text-primary">Address</h2>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <Input
                  label="Address Line 1"
                  value={formData.address_line_1}
                  onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                  placeholder="Street address"
                  fullWidth
                />
                <Input
                  label="Address Line 2"
                  value={formData.address_line_2}
                  onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                  placeholder="Apt, suite, unit, etc. (optional)"
                  fullWidth
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                <Select
                  label="Country"
                  value={formData.country_id || 0}
                  onChange={(e) => handleCountryChange(Number(e.target.value))}
                  options={[
                    { label: 'Select Country', value: 0 },
                    ...countries.filter(c => c.countrycode !== 'ALL').map(c => ({ label: c.countryname, value: c.countryid }))
                  ]}
                  fullWidth
                />
                <Select
                  label="State/Province"
                  value={formData.state_id || 0}
                  onChange={(e) => handleStateChange(Number(e.target.value))}
                  options={[
                    { label: isLoadingStates ? 'Loading...' : 'Select State', value: 0 },
                    ...states.filter(s => s.statecode !== 'ALL').map(s => ({ label: s.statename, value: s.stateid }))
                  ]}
                  disabled={!formData.country_id || isLoadingStates}
                  fullWidth
                />
                <Select
                  label="City"
                  value={formData.city_id || 0}
                  onChange={(e) => handleInputChange('city_id', Number(e.target.value))}
                  options={[
                    { label: isLoadingCities ? 'Loading...' : 'Select City', value: 0 },
                    ...cities.filter(c => c.cityname !== 'All').map(c => ({ label: c.cityname, value: c.cityid }))
                  ]}
                  disabled={!formData.state_id || isLoadingCities}
                  fullWidth
                />
                <Input
                  label="Postal Code"
                  value={formData.postal_code}
                  onChange={(e) => handleInputChange('postal_code', e.target.value)}
                  placeholder="e.g., T2P 1A1"
                  fullWidth
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <Select
                  label="Business Type"
                  value={formData.business_type_id || 0}
                  onChange={(e) => handleBusinessTypeChange(Number(e.target.value))}
                  options={[
                    { label: 'Select Business Type', value: 0 },
                    ...businessTypes.map(bt => ({ label: bt.value, value: bt.key }))
                  ]}
                  fullWidth
                />
                <Select
                  label="Sub Business Type"
                  value={formData.sub_business_id || 0}
                  onChange={(e) => handleInputChange('sub_business_id', Number(e.target.value) || undefined)}
                  options={[
                    { label: isLoadingSubBusiness ? 'Loading...' : 'Select Sub Business (Optional)', value: 0 },
                    ...subBusinessTypes.map(sb => ({ label: sb.value, value: sb.key }))
                  ]}
                  disabled={isLoadingSubBusiness || !formData.business_type_id}
                  fullWidth
                />
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate('/tenants')}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditTenantPage;
